import React, { useState } from 'react';
import TableLayout from './TableLayout';
import './FloorPlan.css';

const FloorPlan = ({ tables, onUpdatePosition, onUpdateStatus }) => {
  const [selectedFloor, setSelectedFloor] = useState('Ground Floor');
  const [viewMode, setViewMode] = useState('layout'); // 'layout' or 'grid'

  const floors = [
    { name: 'Ground Floor', icon: '🏢', color: '#3498db' },
    { name: 'First Floor', icon: '🏢', color: '#e74c3c' },
    { name: 'Second Floor', icon: '🏢', color: '#f39c12' },
    { name: 'Rooftop', icon: '☀️', color: '#9b59b6' },
    { name: 'Outdoor', icon: '🌳', color: '#2ecc71' }
  ];

  const getFloorTables = (floorName) => {
    return tables.filter(t => t.floor === floorName);
  };

  const getFloorStats = (floorName) => {
    const floorTables = getFloorTables(floorName);
    const total = floorTables.length;
    const available = floorTables.filter(t => t.status === 'available').length;
    const occupied = floorTables.filter(t => t.status === 'occupied').length;
    const capacity = floorTables.reduce((sum, t) => sum + (t.capacity || 0), 0);
    return { total, available, occupied, capacity };
  };

  const currentFloorTables = getFloorTables(selectedFloor);
  const currentFloor = floors.find(f => f.name === selectedFloor);

  return (
    <div className="floor-plan-container">
      <div className="floor-selector">
        {floors.map(floor => {
          const stats = getFloorStats(floor.name);
          const isActive = selectedFloor === floor.name;
          
          return (
            <button
              key={floor.name}
              className={`floor-btn ${isActive ? 'active' : ''}`}
              onClick={() => setSelectedFloor(floor.name)}
              style={{
                borderLeftColor: isActive ? floor.color : 'transparent'
              }}
            >
              <div className="floor-btn-header">
                <span className="floor-icon">{floor.icon}</span>
                <span className="floor-name">{floor.name}</span>
              </div>
              <div className="floor-stats-mini">
                <span className="stat-mini">
                  <strong>{stats.total}</strong> tables
                </span>
                <span className="stat-mini">
                  <strong>{stats.available}</strong> available
                </span>
                <span className="stat-mini">
                  <strong>{stats.capacity}</strong> capacity
                </span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="floor-content">
        <div className="floor-header" style={{borderBottomColor: currentFloor?.color}}>
          <div className="floor-title">
            <span className="floor-icon-large">{currentFloor?.icon}</span>
            <div>
              <h2>{selectedFloor}</h2>
              <p>{currentFloorTables.length} tables | {getFloorStats(selectedFloor).capacity} total capacity</p>
            </div>
          </div>
          
          <div className="view-toggle">
            <button
              className={`toggle-btn ${viewMode === 'layout' ? 'active' : ''}`}
              onClick={() => setViewMode('layout')}
            >
              📐 Layout View
            </button>
            <button
              className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
            >
              🔲 Grid View
            </button>
          </div>
        </div>

        {viewMode === 'layout' ? (
          <div className="floor-layout-view">
            {currentFloorTables.length > 0 ? (
              <TableLayout 
                tables={currentFloorTables}
                onUpdatePosition={onUpdatePosition}
                onUpdateStatus={onUpdateStatus}
              />
            ) : (
              <div className="empty-floor">
                <div className="empty-floor-icon">🪑</div>
                <h3>No tables on this floor</h3>
                <p>Add tables to {selectedFloor} to get started</p>
              </div>
            )}
          </div>
        ) : (
          <div className="floor-grid-view">
            <div className="tables-grid">
              {currentFloorTables.map(table => {
                const statusColors = {
                  available: '#2ecc71',
                  occupied: '#e74c3c',
                  reserved: '#f39c12',
                  cleaning: '#9b59b6'
                };
                
                return (
                  <div 
                    key={table.id} 
                    className={`table-grid-card status-${table.status}`}
                    style={{borderTopColor: statusColors[table.status]}}
                  >
                    <div className="table-grid-header">
                      <h3>Table {table.tableNumber}</h3>
                      <span 
                        className="status-indicator"
                        style={{backgroundColor: statusColors[table.status]}}
                      ></span>
                    </div>
                    <div className="table-grid-details">
                      <p><strong>Capacity:</strong> {table.capacity} people</p>
                      <p><strong>Section:</strong> {table.section}</p>
                      <p><strong>Status:</strong> <span className="status-text">{table.status}</span></p>
                    </div>
                    <div className="table-grid-actions">
                      <select 
                        value={table.status}
                        onChange={(e) => onUpdateStatus(table.id, e.target.value)}
                        className="status-select-compact"
                      >
                        <option value="available">Available</option>
                        <option value="occupied">Occupied</option>
                        <option value="reserved">Reserved</option>
                        <option value="cleaning">Cleaning</option>
                      </select>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="floor-summary">
        <h4>Floor Summary</h4>
        <div className="summary-grid">
          {floors.map(floor => {
            const stats = getFloorStats(floor.name);
            if (stats.total === 0) return null;
            
            return (
              <div key={floor.name} className="summary-card">
                <div className="summary-header" style={{background: floor.color}}>
                  <span>{floor.icon}</span>
                  <span>{floor.name}</span>
                </div>
                <div className="summary-stats">
                  <div className="summary-stat">
                    <span className="stat-value">{stats.total}</span>
                    <span className="stat-label">Tables</span>
                  </div>
                  <div className="summary-stat">
                    <span className="stat-value">{stats.available}</span>
                    <span className="stat-label">Available</span>
                  </div>
                  <div className="summary-stat">
                    <span className="stat-value">{stats.occupied}</span>
                    <span className="stat-label">Occupied</span>
                  </div>
                  <div className="summary-stat">
                    <span className="stat-value">{stats.capacity}</span>
                    <span className="stat-label">Capacity</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FloorPlan;
